/**
 * Essentially a <textarea> with a text label above, with an optional
 * error message (which causes the field to turn red).
 */

// TODO(david): This can be merged with the almost-identical KUILabeledField.

import {css, StyleSheet} from "aphrodite";
import classNames from "classnames";
import {useId} from "react";

import {color, fade} from "@khanacademy/wonder-blocks-tokens";
import {
    Caption,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";

type KUITextAreaProps = {
    autoFocus?: boolean;
    caption?: React.ReactNode;
    className?: string;
    defaultValue?: string;
    disabled?: boolean;
    error?: string;
    height?: string | number;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onClick?: (e: React.MouseEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    placeholder?: string;
    rows?: number;
    title: string;
    description?: string;
    value?: string;
};

const KUITextArea = (props: KUITextAreaProps): React.ReactNode => {
    const {
        className,
        title,
        description,
        error,
        autoFocus,
        disabled,
        defaultValue,
        placeholder,
        value,
        rows,
        onChange,
        height,
        onClick,
        onKeyDown,
        caption,
    } = props;
    const textAreaId = useId();
    const labelId = useId();

    return (
        <div className={classNames(css(styles.default), className)}>
            <LabelMedium id={labelId} htmlFor={textAreaId} tag="label">
                {title}
            </LabelMedium>
            {description != null && (
                <LabelSmall style={styles.description}>
                    {description}
                </LabelSmall>
            )}
            {error && <div className={css(styles.error)}>{error}</div>}
            <textarea
                id={textAreaId}
                aria-labelledby={labelId}
                autoFocus={autoFocus}
                className={css(styles.input, error && styles.inputError)}
                disabled={disabled}
                defaultValue={defaultValue}
                placeholder={placeholder}
                value={value}
                rows={rows}
                onChange={onChange}
                onClick={onClick}
                onKeyDown={onKeyDown}
                style={{
                    height,
                }}
            />
            {caption && <Caption style={styles.caption}>{caption}</Caption>}
        </div>
    );
};

const errorColor = color.red;

const styles = StyleSheet.create({
    default: {
        lineHeight: 1.4,
        marginBottom: 20,
    },

    description: {
        marginTop: 4,
        marginBottom: 4,
        color: color.offBlack64,
    },

    // Error message falls below title if there isn't horizontal room for
    // both pieces of text
    error: {
        color: errorColor,
        float: "right",
        marginLeft: 20,
        textAlign: "right",
    },

    input: {
        background: "#fcfcfc",
        border: `1px solid ${color.offBlack50}`,
        boxSizing: "border-box",
        clear: "both",
        font: "inherit",
        padding: "8px 10px",
        margin: "5px 0 0",
        verticalAlign: "baseline",
        width: "100%",

        ":focus": {
            outline: "none",
            boxShadow: `0px 0px 10px ${fade(color.green, 0.8)}`,
        },
    },

    inputError: {
        background: fade(color.red, 0.16),
        borderColor: errorColor,

        ":focus": {
            boxShadow: `0px 0px 10px ${fade(errorColor, 0.4)}`,
        },
    },

    caption: {
        textAlign: "right",
    },
});

export default KUITextArea;
